/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react';
import clamp from 'lodash/clamp';
import { useSpring } from 'react-spring';
import { useGesture } from 'react-use-gesture';
import { useGalleryGestureLock } from '../context/GalleryGestureLock';
import { GalleryImage } from '../Gallery.types';
import useContainerMeasurement from 'shared/hooks/useContainerMeasurement';

const calculateViewportOffset = (
    slideIndex: number,
    viewportWidth: number,
    additionalOffset = 0
) => {
    return -(slideIndex * viewportWidth) + additionalOffset;
};

const useGallery = (
    images: GalleryImage[],
    initialSlideIndex: number,
    viewportRef: React.RefObject<HTMLDivElement>,
    allowMotion: boolean,
    onSlideNext?: (slideIndex: number) => void,
    onSlidePrevious?: (slideIndex: number) => void,
    onSlideChanged?: (slideIndex: number) => void
) => {
    const [slideIndex, setSlide] = useState(initialSlideIndex);
    const gestureLock = useGalleryGestureLock();

    const numSlides = images.length;
    const isFirstSlide = slideIndex === 0;
    const isLastSlide = slideIndex === numSlides - 1;

    // Viewport offset position must be recalculated if the gallery viewport element changes size
    const [viewportWidth, viewportHeight] = useContainerMeasurement(
        viewportRef
    );

    // This spring controls the value we use to offset the slide container within
    // the viewport. We can use it to either jump to a specfic offset immediately
    // or to animate from the current offset to another using spring-based motion
    const [{ viewportOffset }, setSpring] = useSpring(() => {
        // Initialise viewport offset to the initial slide
        return {
            viewportOffset: calculateViewportOffset(
                initialSlideIndex,
                viewportWidth
            )
        };
    });

    // Reset gallery state if its images change
    useEffect(() => {
        setSlide(initialSlideIndex);
        setViewportOffset(0);
    }, [images]);

    const setViewportOffset = (
        index: number,
        offset = 0,
        isAnimated = false,
        initialVelocity = 0
    ) => {
        const viewportOffset = calculateViewportOffset(
            index,
            viewportWidth,
            offset
        );

        setSpring({
            viewportOffset: viewportOffset,
            immediate: !isAnimated || !allowMotion,
            config: {
                velocity: initialVelocity,
                tension: 300,
                mass: 0.5
            }
        });
    };

    // Set the viewport offset immediately to the requested slide + offset
    const setViewportToSlide = (index: number, offset = 0) => {
        setViewportOffset(index, offset);
    };

    // Animate the viewport offset to the requested slide
    const animateViewportToSlide = (index: number, initialVelocity = 0) => {
        setViewportOffset(index, 0, true, initialVelocity);
    };

    const showSlide = (nextSlideIndex = 0) => {
        setSlide(nextSlideIndex);
        setViewportToSlide(nextSlideIndex, 0);
        onSlideChanged && onSlideChanged(nextSlideIndex);
    };

    const showNextSlide = (
        initialVelocity = 0,
        dragDistancePriorToRelease = 0
    ) => {
        let nextSlideIndex = slideIndex + 1;

        if (isLastSlide) {
            // We're looping from the end to the beginning.
            //
            // Immediately switch the viewport offset from this cloned first slide
            // (placed at the end of the real slides) to the real first slide,
            // being sure to include the exact offset the user had dragged to before releasing
            // so it all seamlessly lines up
            setViewportToSlide(-1, dragDistancePriorToRelease);

            // We've looped round so the next slide is actually the first now
            nextSlideIndex = 0;
        }

        setSlide(nextSlideIndex);
        animateViewportToSlide(nextSlideIndex, initialVelocity);

        // Note: we're passing the slide we've transitioned from, not to
        onSlideNext && onSlideNext(slideIndex);
        onSlideChanged && onSlideChanged(nextSlideIndex);
    };

    const showPreviousSlide = (
        initialVelocity = 0,
        dragDistancePriorToRelease = 0
    ) => {
        let previousSlideIndex = slideIndex - 1;

        if (isFirstSlide) {
            // We're looping from the beginning to the end.
            //
            // Immediately switch the viewport offset from this cloned last slide
            // (placed at the beginning of the real slides) to the real last slide,
            // being sure to include the exact offset the user had dragged before releasing
            // so it all seamlessly lines up
            setViewportToSlide(numSlides, dragDistancePriorToRelease);

            // We've looped round so the previous slide is actually the last now
            previousSlideIndex = numSlides - 1;
        }

        setSlide(previousSlideIndex);
        animateViewportToSlide(previousSlideIndex, initialVelocity);

        // Note: we're passing the slide we've transitioned from, not to
        onSlidePrevious && onSlidePrevious(slideIndex);
        onSlideChanged && onSlideChanged(previousSlideIndex);
    };

    // This gesture hook is used to allow the user to touch-drag the slides within
    // the viewport as well as swipe left and right. We're essentially just
    // interpreting touch events and mapping to a movement of the viewport spring
    const touchEventHandlers = useGesture(
        {
            onDrag: ({
                down,
                movement: [x],
                swipe: [swipeX],
                vxvy: [velocityX],
                first: isFirstTouch,
                last: isLastTouch
            }) => {
                const dragDistanceX = clamp(x, -viewportWidth, viewportWidth);
                const triggerPercentageDrag = viewportWidth * 0.2;

                if (isFirstTouch && !gestureLock.lock('drag slide')) {
                    return;
                }

                if (gestureLock.getActiveInteraction() !== 'drag slide') {
                    return;
                }

                // User has completed the touch action
                if (isLastTouch || !down) {
                    const isDraggedToRight =
                        dragDistanceX < -triggerPercentageDrag;
                    const isDraggedToLeft =
                        dragDistanceX > triggerPercentageDrag;
                    const isSwipedToRight = swipeX === -1;
                    const isSwipedToLeft = swipeX === 1;

                    if (isDraggedToRight || isSwipedToRight) {
                        showNextSlide(velocityX, dragDistanceX);
                    } else if (isDraggedToLeft || isSwipedToLeft) {
                        showPreviousSlide(velocityX, dragDistanceX);
                    } else {
                        // User did not drag far enough - bounce back so that
                        // the current slide remains visible
                        animateViewportToSlide(slideIndex, velocityX);
                    }

                    gestureLock.unlock('drag slide');
                }

                // We're in the middle of a touch action - move the viewport offset relative
                // to the distance dragged
                else {
                    setViewportToSlide(slideIndex, dragDistanceX);
                }
            }
        },
        {
            drag: {
                axis: 'x',
                swipeDistance: [3, 3],
                swipeVelocity: [0, 0],
                bounds: {
                    left: numSlides === 1 ? 0 : -Infinity,
                    right: numSlides === 1 ? 0 : Infinity
                },
                filterTaps: true,
                rubberband: true
            }
        }
    );

    // Ensure the viewport offset is recalculated whenever the viewport dimensions change
    useEffect(() => {
        setViewportToSlide(slideIndex);
    }, [viewportWidth]);

    return {
        slideIndex,
        viewportOffset,
        viewportWidth,
        viewportHeight,
        controls: {
            // We're wrapping the previous/next methods so as not to expose
            // the optional parameters we use internally
            previous: () => showPreviousSlide(),
            next: () => showNextSlide(),
            set: showSlide
        },
        touchEventHandlers
    };
};

export default useGallery;
